import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchAuthenticatedContentfulEntriesCached } from 'Apis/contentfulProxy';
import { RootState } from 'Rdx/store';

/**
 * Mirrors API ENABLE_RISK_ADVISOR_GRID_PERF (Contentful backendContent).
 *
 * @deprecated Remove when perf is default: delete this hook and always use deferred count
 * loading in FindingsGrid (skipCount + countOnly). Until then, legacy single-fetch + estimate
 * UX remains when enabled is false.
 */
const ENABLE_RISK_ADVISOR_GRID_PERF = 'ENABLE_RISK_ADVISOR_GRID_PERF';
const FALSE_VALUES = new Set(['false', '0', 'no', 'off']);

function parseBooleanFlag(value: unknown, defaultValue = false): boolean {
    if (value === true || value === false) {
        return value;
    }
    const normalized = `${value ?? ''}`.trim().toLowerCase();
    if (normalized === '') {
        return defaultValue;
    }
    return !FALSE_VALUES.has(normalized);
}

function getPerfFlagFromResponse(response: { items?: Array<{ fields?: { key?: string; value?: string } }> }): boolean {
    const entry = response?.items?.find((item) => item?.fields?.key === ENABLE_RISK_ADVISOR_GRID_PERF);
    return parseBooleanFlag(entry?.fields?.value, false);
}

export function useRiskAdvisorGridPerf(): { enabled: boolean; loading: boolean } {
    const token = useSelector((state: RootState) => state.auth.token);
    const [enabled, setEnabled] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const envOverride = process.env.REACT_APP_ENABLE_RISK_ADVISOR_GRID_PERF;
        if (envOverride !== undefined) {
            setEnabled(parseBooleanFlag(envOverride, false));
            setLoading(false);
            return;
        }

        if (!token) {
            setEnabled(false);
            setLoading(false);
            return;
        }

        let cancelled = false;
        fetchAuthenticatedContentfulEntriesCached({ ['content_type']: 'backendContent' }, token)
            .then((response: { items?: Array<{ fields?: { key?: string; value?: string } }> }) => {
                if (cancelled) {
                    return;
                }
                setEnabled(getPerfFlagFromResponse(response));
            })
            .catch(() => {
                if (!cancelled) {
                    setEnabled(false);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [token]);

    return { enabled, loading };
}
